import React, { useEffect, useState } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { logoutLocal } from "@services/auth";

const PageContainer = styled(Box)({
  backgroundColor: "#ffffff",
  width: "100%",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
});

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 100.66px at 298.8px 182.49px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 8.9043px at 1471.2px 533.38px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 84.635px at 682.2px 1290.7px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});

const GradientBackground = styled(Box)({
  position: "absolute",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0) 0%, #e9fbfb 69.712%, rgba(255,255,255,0) 100%)",
  height: "762px",
  left: "1px",
  top: 0,
  width: "100%",
  pointerEvents: "none",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#f4feff",
  border: "1px solid #5bbfd6",
  borderRadius: "16px",
  padding: "48px",
  width: "585px",
  maxWidth: "calc(100vw - 32px)",
  position: "relative",
  boxShadow: "0px 4px 24px 0px rgba(191,229,236,0.25)",
  zIndex: 1,
});

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "20px",
  width: "100%",
  maxWidth: "489px",
  margin: "0 auto",
});

const IconContainer = styled(Box)({
  width: "101px",
  height: "101px",
  borderRadius: "50%",
  backgroundColor: "#c2410c",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "8px",
});

const Title = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#c2410c",
  textAlign: "center",
  marginBottom: "4px",
});

const Description = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "1.5",
  color: "#033843",
  textAlign: "center",
  maxWidth: "100%",
});

const PrimaryButton = styled(Button)({
  background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
  border: "1px solid #ffffff",
  borderRadius: "60px",
  minHeight: "52px",
  width: "100%",
  maxWidth: "280px",
  padding: "12px 24px",
  boxShadow:
    "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  color: "#ffffff",
  "&:hover": {
    background: "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)",
  },
});

const SecondaryButton = styled(Button)({
  borderRadius: "60px",
  minHeight: "48px",
  width: "100%",
  maxWidth: "280px",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "15px",
  border: "1px solid #0a829d",
  color: "#0a829d",
  "&:hover": {
    backgroundColor: "rgba(10, 130, 157, 0.06)",
  },
});

const TextButton = styled(Button)({
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  color: "#737373",
  marginTop: "8px",
});

export interface LicenseInactiveProps {
  onCheckAgain: () => Promise<void>;
}

export default function LicenseInactive({ onCheckAgain }: LicenseInactiveProps) {
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCheckAgain = async () => {
    setRetrying(true);
    try {
      await onCheckAgain();
    } finally {
      setRetrying(false);
    }
  };

  const handleBilling = () => {
    navigate("/dashboard/billing");
  };

  const handleSignOut = async () => {
    await logoutLocal();
    window.location.href = "/login";
  };

  return (
    <PageContainer>
      <BackgroundOverlay />
      <GradientBackground />

      <ContentContainer>
        <ContentWrapper>
          <IconContainer>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="#ffffff"
              aria-hidden
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          </IconContainer>

          <Title>Subscription inactive</Title>

          <Description>
            Your organization&apos;s UniComply license is not active. Access to the platform is
            paused until the subscription is renewed. Payment or a billing update may be required in
            Master Configuration Manager or your account billing settings.
          </Description>

          <Description sx={{ fontSize: "14px", color: "#404040" }}>
            If you believe this is a mistake, contact support. You can check again after your
            administrator has restored the subscription.
          </Description>

          <PrimaryButton onClick={handleCheckAgain} disabled={retrying}>
            {retrying ? "Checking…" : "Check again"}
          </PrimaryButton>

          <SecondaryButton onClick={handleBilling}>Go to billing</SecondaryButton>

          <TextButton onClick={handleSignOut}>Sign out</TextButton>
        </ContentWrapper>
      </ContentContainer>
    </PageContainer>
  );
}
